import { ColumnProps } from "@monolith-forensics/monolith-ui";
import moment from "moment";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { ExternalLink } from "lucide-react";

import { formatCurrency } from "@/utils/currency-formatter";
import { LabAsset, LabAssetType } from "../labAssets.types";
import {
  getLabAssetDisplayName,
  getValidationDueStatus,
} from "../labAssets.utils";
import { ValidationDueBadge } from "../components/ValidationDueBadge";
import { AssetTypeBadge } from "../components/AssetTypeBadge";

// The asset-number cell is the only way into a row's details page and
// `asset_number` is nullable, so a row without one gets a visible "View" label
// with the launch icon always shown instead of an empty, hover-only link.
const AssetNumberCell = styled(
  ({ className, rowData }: { className?: string; rowData: LabAsset }) => {
    const hasAssetNumber = !!rowData.asset_number;

    return (
      <div className={className}>
        <Link to={`/assets/${rowData.uuid}/overview`}>
          <div
            className={
              hasAssetNumber
                ? "asset-number-container"
                : "asset-number-container asset-number-container--placeholder"
            }
          >
            {hasAssetNumber ? rowData.asset_number : "View"}
            <ExternalLink size={14} />
          </div>
        </Link>
      </div>
    );
  }
)`
  width: fit-content;
  .asset-number-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
    font-weight: 500;
    color: ${(props) => props.theme.palette.primary.main};

    svg {
      display: none;
    }

    &:hover {
      text-decoration: underline;
      cursor: pointer;

      svg {
        display: block;
      }
    }
  }

  .asset-number-container--placeholder svg {
    display: block;
  }
`;

const renderDate = (value: string | null) =>
  value ? moment(value).format("YYYY-MM-DD") : "";

// The date, wrapped in the due/overdue badge colours when it is inside the
// window (ENG-1680). Status is derived from rowData at render time, so the
// row re-renders with the API value and never closes over external state
// (mfui copies column props once at mount). Font size follows the cell so
// badged and plain dates read at the same size.
const DueDateBadge = styled(ValidationDueBadge)`
  font-size: inherit;
`;

const NextValidationDueCell = ({ rowData }: { rowData: LabAsset }) => {
  const date = renderDate(rowData.next_validation_due);
  const status = getValidationDueStatus(rowData.next_validation_due);
  return status ? <DueDateBadge status={status}>{date}</DueDateBadge> : date;
};

/**
 * A column def carrying the asset types it applies to. Colocating the split
 * with the column is what keeps the two from drifting: `assetTypes` is
 * required, so a new column can't be added without declaring where it shows,
 * and `dataField` is narrowed to `keyof LabAsset` so a rename or typo fails
 * `yarn tsc`. Assignable to `ColumnProps`, so the array still feeds `Table`
 * directly; mfui copies unknown keys through untouched.
 */
type LabAssetColumn = Omit<ColumnProps, "dataField"> & {
  dataField: keyof LabAsset;
  assetTypes: LabAssetType[];
};

const ALL_ASSET_TYPES: LabAssetType[] = ["equipment", "software"];

export const LabAssetColumnDefinitions: LabAssetColumn[] = [
  {
    dataField: "asset_number",
    caption: "Asset Number",
    assetTypes: ALL_ASSET_TYPES,
    render: ({ rowData }: { rowData: LabAsset }) => (
      <AssetNumberCell rowData={rowData} />
    ),
  },
  {
    dataField: "asset_type",
    caption: "Asset Type",
    assetTypes: ALL_ASSET_TYPES,
    render: ({ rowData }: { rowData: LabAsset }) => (
      <AssetTypeBadge type={rowData.asset_type} />
    ),
  },
  {
    // Equipment-only: software rows keep their display name in `license_name`
    // and leave `name` null, so a surface showing both would render the same
    // value twice through getLabAssetDisplayName.
    dataField: "name",
    caption: "Name",
    assetTypes: ["equipment"],
    render: ({ rowData }: { rowData: LabAsset }) =>
      getLabAssetDisplayName(rowData),
  },
  {
    dataField: "vendor",
    caption: "Vendor",
    assetTypes: ALL_ASSET_TYPES,
  },
  {
    dataField: "category",
    caption: "Category",
    assetTypes: ["equipment"],
  },
  {
    dataField: "model",
    caption: "Model",
    assetTypes: ["equipment"],
    visible: false,
  },
  {
    dataField: "serial_number",
    caption: "Serial Number",
    assetTypes: ["equipment"],
  },
  {
    dataField: "purchase_date",
    caption: "Purchase Date",
    dataType: "date",
    assetTypes: ALL_ASSET_TYPES,
    render: ({ rowData }: { rowData: LabAsset }) =>
      renderDate(rowData.purchase_date),
  },
  {
    dataField: "cost",
    caption: "Cost",
    assetTypes: ALL_ASSET_TYPES,
    render: ({ rowData }: { rowData: LabAsset }) =>
      rowData.cost === null ? "" : formatCurrency(rowData.cost),
  },
  {
    dataField: "location",
    caption: "Location",
    assetTypes: ALL_ASSET_TYPES,
  },
  {
    // dataField is the sortable API column (sorted server-side by assignee
    // name); the render shows the joined user instead of the bare id.
    dataField: "issued_to_id",
    caption: "Issued To",
    assetTypes: ALL_ASSET_TYPES,
    render: ({ rowData }: { rowData: LabAsset }) =>
      rowData.issued_to?.full_name ?? "",
  },
  {
    dataField: "barcode",
    caption: "Barcode",
    assetTypes: ["equipment"],
    visible: false,
  },
  {
    dataField: "edition",
    caption: "Edition",
    assetTypes: ["software"],
    visible: false,
  },
  {
    // Software-only, for the same reason `name` is equipment-only.
    dataField: "license_name",
    caption: "License Name",
    assetTypes: ["software"],
    visible: false,
  },
  {
    dataField: "license_key",
    caption: "License Key",
    assetTypes: ["software"],
    visible: false,
  },
  {
    dataField: "dongle_sn",
    caption: "Dongle S/N",
    assetTypes: ["software"],
    visible: false,
  },
  {
    dataField: "expire_date",
    caption: "Expiration Date",
    dataType: "date",
    assetTypes: ["software"],
    visible: false,
    render: ({ rowData }: { rowData: LabAsset }) =>
      renderDate(rowData.expire_date),
  },
  {
    dataField: "next_validation_due",
    caption: "Next Validation Due",
    dataType: "date",
    assetTypes: ALL_ASSET_TYPES,
    render: ({ rowData }: { rowData: LabAsset }) => (
      <NextValidationDueCell rowData={rowData} />
    ),
  },
  {
    dataField: "sms",
    caption: "SMS",
    assetTypes: ["software"],
    visible: false,
  },
  {
    dataField: "description",
    caption: "Description",
    assetTypes: ALL_ASSET_TYPES,
    visible: false,
  },
];

/**
 * The fields relevant to each asset type, derived from the `assetTypes` tag on
 * each column def (the split itself came from the legacy per-type column defs:
 * Equipment/EquipmentColumnDefs.ts vs ColumnDefinitions/
 * ForensicSoftwareColumnDefs.js). Consumed by the flyout (ENG-1668); reusable
 * by the type-aware forms (ENG-1381) and details page (ENG-1382).
 *
 * The seed is written out per type rather than built from LabAssetType so that
 * adding a type fails `yarn tsc` here instead of silently yielding no fields.
 */
export const LAB_ASSET_FIELDS_BY_TYPE: Record<
  LabAssetType,
  Set<keyof LabAsset>
> = LabAssetColumnDefinitions.reduce(
  (acc, col) => {
    col.assetTypes.forEach((assetType) => acc[assetType].add(col.dataField));
    return acc;
  },
  {
    equipment: new Set<keyof LabAsset>(),
    software: new Set<keyof LabAsset>(),
  }
);
